import React, { useState, useMemo, useEffect } from 'react';
import { useHeader } from '../contexts/HeaderContext';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PlusIcon, ChartBarIcon, ClockIcon, CheckCircleIcon, MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { assessmentService } from '../services/assessmentService';
import { questionnaireService } from '../services/questionnaireService';
import { clientService } from '../services/clientService';
import { useAuth } from '../contexts/AuthContext';
import ClientLookup from '../components/ClientLookup';
import LoadingSpinner from '../components/LoadingSpinner';
import { Pagination } from '../components/Pagination';
import { Assessment, Client } from '../types';
import toast from 'react-hot-toast';
import GoalInput, { GOAL_OPTIONS } from '../components/GoalInput';

const Assessments: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setTitle } = useHeader();

  useEffect(() => {
    setTitle('Evaluations');
  }, [setTitle]);

  // Use custom claims permissions instead of role checks
  const hasClientsLookup = user?.permissions?.includes('clients:lookup') || user?.permissions?.includes('admin:full');
  const hasClientsAttachAdvisor = user?.permissions?.includes('clients:attach_advisor') || user?.permissions?.includes('admin:full');
  const hasAssessmentsWrite = user?.permissions?.includes('assessments:write') || user?.permissions?.includes('admin:full');

  // Check if user is a restricted client (standard user, not admin)
  const isRestrictedClient = user?.role === 'client' || user?.role === 'CLIENT_USER';

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedClientName, setSelectedClientName] = useState('');
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState('');
  const [assessmentGoals, setAssessmentGoals] = useState({ primary: '', secondary: '' });

  // Filtering state
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [clientFilter, setClientFilter] = useState(searchParams.get('clientId') || 'all');
  const [questionnaireFilter, setQuestionnaireFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const queryClient = useQueryClient();

  // Reset page to 1 on filter/search changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, clientFilter, questionnaireFilter, sortBy, sortOrder, limit]);

  // Fetch assessments
  const { data: assessmentsData, isLoading: assessmentsLoading } = useQuery({
    queryKey: ['assessments', {
      page,
      limit,
      status: statusFilter,
      clientId: clientFilter,
      questionnaireLabel: questionnaireFilter,
      sortBy,
      sortOrder,
      search: searchTerm,
    }],
    queryFn: () => assessmentService.getAssessments({
      page,
      limit,
      status: statusFilter === 'all' ? undefined : statusFilter,
      clientId: clientFilter === 'all' ? undefined : parseInt(clientFilter),
      questionnaireLabel: questionnaireFilter === 'all' ? undefined : questionnaireFilter,
      sortBy,
      sortOrder,
      search: searchTerm,
    }),
  });

  // Call-ahead prefetching in background
  useEffect(() => {
    if (assessmentsData?.pagination && page < assessmentsData.pagination.pages) {
      const nextPage = page + 1;
      queryClient.prefetchQuery({
        queryKey: ['assessments', {
          page: nextPage,
          limit,
          status: statusFilter,
          clientId: clientFilter,
          questionnaireLabel: questionnaireFilter,
          sortBy,
          sortOrder,
          search: searchTerm,
        }],
        queryFn: () => assessmentService.getAssessments({
          page: nextPage,
          limit,
          status: statusFilter === 'all' ? undefined : statusFilter,
          clientId: clientFilter === 'all' ? undefined : parseInt(clientFilter),
          questionnaireLabel: questionnaireFilter === 'all' ? undefined : questionnaireFilter,
          sortBy,
          sortOrder,
          search: searchTerm,
        }),
      });
    }
  }, [page, limit, statusFilter, clientFilter, questionnaireFilter, sortBy, sortOrder, searchTerm, assessmentsData, queryClient]);

  // Fetch clients for dropdown
  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientService.getClients(),
    enabled: !isRestrictedClient,
  });

  // Fetch questionnaires for dropdown
  const { data: questionnairesData, isLoading: questionnairesLoading } = useQuery({
    queryKey: ['questionnaires'],
    queryFn: () => questionnaireService.getQuestionnaires(),
    enabled: !isRestrictedClient,
  });

  const assessments = assessmentsData?.assessments || [];
  const clients = clientsData?.clients || [];
  const questionnaires = questionnairesData || [];

  // Extract unique questionnaire labels from loaded questionnaires
  const uniqueQuestionnaireLabels = useMemo(() => {
    const labels = new Set<string>();
    questionnaires.forEach((q: any) => {
      if (q.label) {
        labels.add(q.label);
      }
    });
    return Array.from(labels).sort();
  }, [questionnaires]);


  // Attach advisor to client mutation (for advisors selecting clients they don't have access to)
  const attachAdvisorMutation = useMutation({
    mutationFn: clientService.attachAdvisor,
    onSuccess: () => {
      queryClient.invalidateQueries(['clients'], { refetchActive: true, refetchInactive: true });
      toast.success('Successfully attached to client');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to attach to client');
    },
  });

  // Create assessment mutation
  const createAssessmentMutation = useMutation({
    mutationFn: assessmentService.createAssessment,
    onSuccess: () => {
      queryClient.invalidateQueries(['assessments'], { refetchActive: true, refetchInactive: true });
      setShowCreateForm(false);
      setSelectedClient('');
      setSelectedClientName('');
      setSelectedQuestionnaire('');
      setAssessmentGoals({ primary: '', secondary: '' });
    },
  });

  const handleCreateAssessment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClient || !assessmentGoals.primary) return;

    createAssessmentMutation.mutate({
      clientId: parseInt(selectedClient),
      goals: assessmentGoals,
      questionnaireId: selectedQuestionnaire || undefined,
      questionnaireVersion: selectedQuestionnaire ? undefined : undefined, // Will use active version
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'IN_PROGRESS':
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
      case 'IN_REVIEW':
        return <ClockIcon className="w-5 h-5 text-orange-500" />;
      case 'NEW':
        return <ChartBarIcon className="w-5 h-5 text-gray-400" />;
      case 'ARCHIVED':
        return <ChartBarIcon className="w-5 h-5 text-purple-400" />;
      default:
        return <ChartBarIcon className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'IN_PROGRESS':
        return 'bg-yellow-100 text-yellow-800';
      case 'IN_REVIEW':
        return 'bg-orange-100 text-orange-800';
      case 'NEW':
        return 'bg-gray-100 text-gray-800';
      case 'ARCHIVED':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (assessmentsLoading || (clientsLoading && !isRestrictedClient) || (questionnairesLoading && !isRestrictedClient)) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Evaluations</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Manage agency maturity evaluations
          </p>
        </div>
        {hasAssessmentsWrite && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="w-4 h-4 mr-1.5" />
            New Evaluation
          </button>
        )}
      </div>


      {/* Filter Controls */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0 lg:space-x-3">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search evaluations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-1.5 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block w-full sm:w-auto px-3 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="NEW">New</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="IN_REVIEW">In Review</option>
              <option value="COMPLETED">Completed</option>
              <option value="ARCHIVED">Archived</option>
            </select>

            {/* Client Filter - Hide for restricted clients */}
            {!isRestrictedClient && (
              <select
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                className="block w-full sm:w-auto px-3 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Clients</option>
                {clients.map((client: Client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            )}

            {/* Questionnaire Filter */}
            <select
              value={questionnaireFilter}
              onChange={(e) => setQuestionnaireFilter(e.target.value)}
              className="block w-full sm:w-auto px-3 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Questionnaires</option>
              {uniqueQuestionnaireLabels.map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </select>

            {/* Sort */}
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order as 'asc' | 'desc');
              }}
              className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="createdAt-desc">Newest First</option>
              <option value="createdAt-asc">Oldest First</option>
              <option value="clientName-asc">Client A-Z</option>
              <option value="clientName-desc">Client Z-A</option>
              <option value="status-asc">Status A-Z</option>
              <option value="status-desc">Status Z-A</option>
            </select>
          </div>
        </div>
      </div>

      {/* Assessments List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Evaluations ({assessmentsData?.pagination?.total || 0})
            </h3>
            <div className="flex items-center text-sm text-gray-500">
              <FunnelIcon className="h-4 w-4 mr-1" />
              Filtered results
            </div>
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          {assessments && assessments.length > 0 ? (
            assessments.map((assessment: Assessment) => {
              const lastActivity = assessment.workflowTracking?.[0];
              return (
                <div key={assessment.id} className="relative group px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {getStatusIcon(assessment.status)}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {assessment.client?.name || 'Unknown Client'}
                        </h4>
                        <p className="text-sm text-gray-500 flex items-center space-x-1.5">
                          {assessment.questionnaireLabel && (
                            <>
                              <span className="font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded text-xs">
                                {assessment.questionnaireLabel}
                              </span>
                              <span className="text-gray-300">•</span>
                            </>
                          )}
                          <span>Created {new Date(assessment.createdAt).toLocaleDateString()}</span>
                        </p>
                        {assessment.goals && (
                          <p className="text-xs text-gray-400 mt-1">
                            Goal: {Array.isArray(assessment.goals)
                              ? (assessment.goals[0]?.text || String(assessment.goals[0] || ''))
                              : ((assessment.goals as any).primary || Object.values(assessment.goals)[0] || '')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(assessment.status)}`}>
                        {assessment.status.replace(/_/g, ' ')}
                      </span>
                      <button
                        onClick={() => navigate(`/assessments/${assessment.id}`)}
                        className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                      >
                        View Details
                      </button>
                    </div>
                  </div>

                  {/* Last Activity Tooltip */}
                  <div className="absolute left-12 bottom-full mb-2 w-80 hidden group-hover:block bg-slate-900 text-white rounded-lg shadow-2xl p-3 z-30 pointer-events-none transition-all duration-200 transform -translate-y-1">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                      Last Activity
                    </div>
                    {lastActivity ? (
                      <div className="space-y-1">
                        <div className="text-sm font-semibold text-white">
                          {lastActivity.actionTaken || lastActivity.status.replace(/_/g, ' ')}
                        </div>
                        <div className="flex justify-between items-center text-xs text-slate-300 pt-1 border-t border-slate-800">
                          <span className="font-medium">
                            {lastActivity.user
                              ? `${lastActivity.user.firstName} ${lastActivity.user.lastName}`
                              : 'System'}
                          </span>
                          <span className="text-[11px] text-slate-400">
                            {new Date(lastActivity.createdAt).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-slate-400 italic">
                        No activity log found.
                      </div>
                    )}
                    {/* Tooltip Arrow */}
                    <div className="absolute top-full left-10 -mt-1 w-2.5 h-2.5 bg-slate-900 transform rotate-45"></div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="px-6 py-8 text-center">
              <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No evaluations found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search or filter criteria.
              </p>
            </div>
          )}
        </div>
        {assessmentsData?.pagination && (
          <Pagination
            page={page}
            limit={limit}
            total={assessmentsData.pagination.total}
            totalPages={assessmentsData.pagination.pages}
            onPageChange={setPage}
            onLimitChange={(l) => {
              setLimit(l);
              setPage(1);
            }}
          />
        )}
      </div>

      {/* Create Assessment Modal */}
      {
        showCreateForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Evaluation</h3>
                <form onSubmit={handleCreateAssessment} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Client *</label>
                    {selectedClient ? (
                      <div className="mt-1 flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded-md">
                        <span className="text-sm font-medium text-blue-900">{selectedClientName}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedClient('');
                            setSelectedClientName('');
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Change
                        </button>
                      </div>
                    ) : (
                      <>
                        {hasClientsLookup && (
                          <div className="mt-1 mb-2">
                            <ClientLookup
                              onSelect={async (client) => {
                                if (client.status === 'inactive') {
                                  toast.error('Cannot create evaluation for inactive client');
                                  return;
                                }
                                // Check if user needs to attach to this client (for users with clients:attach_advisor permission)
                                const hasAccess = clients.some((c: Client) => c.id === client.id);
                                if (!hasAccess && hasClientsAttachAdvisor) {
                                  // Advisor doesn't have access, offer to attach
                                  if (window.confirm(`You don't have access to ${client.name}. Attach yourself to this client?`)) {
                                    try {
                                      await attachAdvisorMutation.mutateAsync(client.id);
                                      // After attaching, refresh clients list
                                      queryClient.invalidateQueries({ queryKey: ['clients'] });
                                    } catch (error) {
                                      // Error already handled by mutation
                                      return;
                                    }
                                  } else {
                                    return; // User cancelled attachment
                                  }
                                }
                                setSelectedClient(client.id.toString());
                                setSelectedClientName(client.name);
                              }}
                              excludeClientIds={[]}
                              placeholder="Search for a client (minimum 3 characters)..."
                            />
                          </div>
                        )}
                        <select
                          value={selectedClient}
                          onChange={(e) => {
                            const clientId = e.target.value;
                            setSelectedClient(clientId);
                            const client = clients.find((c: Client) => c.id === parseInt(clientId));
                            if (client) {
                              setSelectedClientName(client.name);
                            }
                          }}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          required
                        >
                          <option value="">Select a client</option>
                          {clients?.map((client: Client) => (
                            <option key={client.id} value={client.id} disabled={client.status === 'inactive'}>
                              {client.name} {client.status === 'inactive' ? '(Inactive)' : ''}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Evaluation Blueprint (Optional)
                    </label>
                    <select
                      value={selectedQuestionnaire}
                      onChange={(e) => setSelectedQuestionnaire(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Use default evaluation blueprint</option>
                      {questionnaires?.map((q: any) => {
                        const activeVersion = q.versions.find((v: any) => v.isActive);
                        return (
                          <option key={q.id} value={q.id}>
                            {q.label} - {activeVersion?.name || 'No active version'} (v{activeVersion?.version || 'N/A'})
                          </option>
                        );
                      })}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Select a specific evaluation blueprint version. If not selected, the default blueprint will be used.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <GoalInput
                      label="Primary Goal"
                      value={assessmentGoals.primary}
                      onChange={(value) => setAssessmentGoals(prev => ({ ...prev, primary: value }))}
                      options={GOAL_OPTIONS}
                      excludeOption={assessmentGoals.secondary}
                      placeholder="Select or type a primary goal..."
                      required
                    />
                    <GoalInput
                      label="Secondary Goal"
                      value={assessmentGoals.secondary}
                      onChange={(value) => setAssessmentGoals(prev => ({ ...prev, secondary: value }))}
                      options={GOAL_OPTIONS}
                      excludeOption={assessmentGoals.primary}
                      placeholder="Select or type a secondary goal..."
                    />
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowCreateForm(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createAssessmentMutation.isLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                    >
                      {createAssessmentMutation.isLoading ? 'Creating...' : 'Create Evaluation'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default Assessments;
